import React, { useState } from 'react'
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  Typography,
  FormHelperText,
  IconButton,
  Tooltip,
  useTheme,
} from '@mui/material'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { alpha } from '@mui/material/styles'
import { ConfigSelectProps } from '../types/EnvironmentConfig.types'

/**
 * ConfigSelect Component
 * A reusable select field component with validation and help text
 */
const ConfigSelect: React.FC<ConfigSelectProps> = ({
  label,
  value,
  onChange,
  options,
  required = false,
  disabled = false,
  error,
  helpText,
  validation,
  multiple = false,
}) => {
  const theme = useTheme()
  const [localError, setLocalError] = useState<string | undefined>(error)
  const [isFocused, setIsFocused] = useState(false)

  const primary = theme.palette.primary.main
  const errorMain = theme.palette.error.main

  const handleChange = (event: any) => {
    const newValue = event.target.value
    onChange(newValue)
  }

  const handleFocus = () => {
    setIsFocused(true)
  }

  const handleBlur = () => {
    setIsFocused(false)
    // Validate on blur
    if (validation && value !== undefined) {
      const validationError = validation(value)
      setLocalError(validationError)
    }
  }

  const fieldsetBorder = localError ? errorMain : isFocused ? primary : theme.palette.divider

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            display: 'flex',
            alignItems: 'center',
            fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
          }}
        >
          {label}
          {required && (
            <Typography
              component="span"
              sx={{ color: 'error.main', ml: 0.5 }}
            >
              *
            </Typography>
          )}
        </Typography>
        {helpText && (
          <Tooltip title={helpText} arrow>
            <IconButton
              size="small"
              sx={{
                ml: 1,
                color: 'text.secondary',
                '&:hover': {
                  color: 'primary.main',
                  bgcolor: alpha(primary, 0.08),
                },
              }}
            >
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <FormControl
        fullWidth
        variant="outlined"
        error={!!localError}
        disabled={disabled}
        sx={{
          '& .MuiOutlinedInput-root': {
            color: 'text.primary',
            fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
            bgcolor: 'background.paper',
            backgroundImage: 'none',
            '& fieldset': {
              borderColor: fieldsetBorder,
            },
            '&:hover fieldset': {
              borderColor: localError ? errorMain : primary,
            },
            '&.Mui-focused fieldset': {
              borderColor: localError ? errorMain : primary,
              borderWidth: 2,
            },
            '&.Mui-disabled': {
              bgcolor: 'action.hover',
              color: 'text.disabled',
            },
          },
          '& .MuiInputLabel-root': {
            color: 'text.secondary',
            fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
            '&.Mui-focused': {
              color: 'primary.main',
            },
            '&.Mui-disabled': {
              color: 'text.disabled',
            },
          },
          '& .MuiSelect-icon': {
            color: 'text.secondary',
          },
        }}
      >
        <Select
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          multiple={multiple}
          displayEmpty
          sx={{
            borderRadius: 2,
            '& .MuiSelect-select': {
              color: 'text.primary',
              fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
            },
          }}
        >
          {options.map((option) => (
            <MenuItem
              key={Array.isArray(option.value) ? option.value.join(',') : option.value}
              value={option.value}
              disabled={option.disabled}
              sx={{
                color: 'text.primary',
                fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                bgcolor: 'background.paper',
                '&:hover': {
                  bgcolor: alpha(primary, 0.06),
                },
                '&.Mui-selected': {
                  bgcolor: alpha(primary, 0.12),
                  color: 'primary.main',
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: alpha(primary, 0.16),
                  },
                },
                '&.Mui-disabled': {
                  color: 'text.disabled',
                  bgcolor: 'action.hover',
                },
              }}
            >
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {localError && (
        <FormHelperText
          error
          sx={{
            color: 'error.main',
            fontSize: '0.75rem',
            mt: 0.5,
            fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
          }}
        >
          {localError}
        </FormHelperText>
      )}

      {helpText && !localError && (
        <FormHelperText
          sx={{
            color: 'text.secondary',
            fontSize: '0.75rem',
            mt: 0.5,
            fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
          }}
        >
          {helpText}
        </FormHelperText>
      )}
    </Box>
  )
}

export default ConfigSelect
